import React, { useEffect, useState } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Container, Grid, InputAdornment, Paper, TextField, Theme, Toolbar, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UserAdminServiceInstance from '../../../services/UserAdminService';
import { IOrganizer } from '../../../models/IOrganizer';
import { IUser } from '../../../models/IUser';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../common/ConfirmDialog';
import OntimeAutocomplete from '../../common/OntimeAutocomplete';
import UserDetailDialog from './UserDetailDialog';
import { format } from 'util';
import axios from 'axios';
import OntimeBreadcrumb from '../../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            backgroundColor: theme.palette.background.paper,
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        organizersPaper: {
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 400,
        },
        organizersList: {
            overflow: 'auto',
            flexGrow: 1,
        },
        appBarSpacer: theme.mixins.toolbar,
        toolbar: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    }));

export default function AdminUserManagement() {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const [organizers, setOrganizers] = useState<IOrganizer[]>([]);
    const [confirmRemove, setConfirmRemove] = useState<IOrganizer | null>(null);
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [organizerFilter, setOrganizerFilter] = useState('');

    const fetchOrganizers = async (cancelToken?: import('axios').CancelToken) => {
        const data = await UserAdminServiceInstance.getOrganizers(true, cancelToken);
        setOrganizers(data);
    };

    useEffect(() => {
        const source = axios.CancelToken.source();
        fetchOrganizers(source.token).catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const handleRemoveRole = async () => {
        if (confirmRemove) {
            await UserAdminServiceInstance.removeOrganizerRole(confirmRemove.userId);
            setConfirmRemove(null);
            await fetchOrganizers();
        }
    };

    const handleUserSelected = (_e: any, value: IUser | null) => {
        if (value) {
            setSelectedUser(value);
        }
    };

    const handleDialogClose = () => {
        setSelectedUser(null);
    };

    const handleUserUpdated = async () => {
        await fetchOrganizers();
    };

    const isOrganizer = (userId: string): boolean => {
        return organizers.some(o => o.userId === userId);
    };

    const filteredOrganizers = organizerFilter
        ? organizers.filter(o => o.name.toLowerCase().includes(organizerFilter.toLowerCase()))
        : organizers;

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    {/* Breadcrumb */}
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{}} current={t('adminUserManagement.title')} />
                    </Grid>
                    {/* User Search Section */}
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <Toolbar className={classes.toolbar}>
                                <Typography variant="h6">{t("adminUserManagement.searchSection")}</Typography>
                            </Toolbar>
                            <OntimeAutocomplete<IUser>
                                id="searchUser"
                                label={t("adminUserManagement.searchUser")}
                                itemIdProperty="userId"
                                itemLabelProperty="name"
                                search={UserAdminServiceInstance.searchUsers}
                                onChange={handleUserSelected}
                            />
                        </Paper>
                    </Grid>

                    {/* Organizers List Section */}
                    <Grid item xs={12}>
                        <Paper className={classes.organizersPaper}>
                            <Toolbar className={classes.toolbar}>
                                <Typography variant="h6">{t("adminUserManagement.organizersSection")}</Typography>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder={t<string>("adminUserManagement.filterOrganizers")}
                                    value={organizerFilter}
                                    onChange={(e) => setOrganizerFilter(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Toolbar>
                            <List className={classes.organizersList}>
                                {filteredOrganizers.map((organizer) => (
                                    <ListItem key={organizer.userId}>
                                        <ListItemAvatar>
                                            <Avatar alt={organizer.name} src={organizer.portraitBlobAddressUri} />
                                        </ListItemAvatar>
                                        <ListItemText primary={organizer.name} />
                                        <ListItemSecondaryAction>
                                            <Tooltip title={t("adminUserManagement.removeRole")}>
                                                <IconButton edge="end" onClick={() => setConfirmRemove(organizer)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>

                {selectedUser && (
                    <UserDetailDialog
                        user={selectedUser}
                        isOrganizer={isOrganizer(selectedUser.userId)}
                        onClose={handleDialogClose}
                        onUserUpdated={handleUserUpdated}
                    />
                )}

                <ConfirmDialog
                    open={confirmRemove !== null}
                    title={t("adminUserManagement.removeRole")}
                    message={confirmRemove ? format(t("adminUserManagement.confirmRemoveRole"), confirmRemove.name) : ""}
                    onAccept={handleRemoveRole}
                    onCancel={() => setConfirmRemove(null)}
                />
            </Container>
        </main>
    );
}
